<template>
  <div class="group-details-view">
    <div v-if="isWaitingForData" data-test="gd-waiting-for-data" style="display: none"></div>
    <div v-if="isGroupDetailsLoaded" class="page-container">
      <AssignLicenceModal
        v-if="memberToAssignLicenceTo"
        :show-modal="showAssignLicenceModal"
        :group-member="memberToAssignLicenceTo"
        @toggle-assign-licence-modal="toggleAssignLicenceModal"
        @assign-licence="assignLicenceToMember"
      />
      <DeleteLicenceModal
        v-if="showLicenceModal"
        :memberWithLicence="memberWithLicence"
        :showLicenceModal="showLicenceModal"
        @toggle-licence-modal="toggleLicenceModal"
        @delete-licence="removeLicenceForMember"
      />
      <EditRoleModal
        v-if="showRoleModal"
        :member="memberToEditRoleFor"
        :showRoleModal="showRoleModal"
        :role="roleToEdit"
        :error="!!roleSubmitError"
        :isAddingRole="isAddingRole"
        @close-role-modal="closeRoleModal"
        @confirm-role="submitEditRole"
      />
      <RemoveNonMemberRoleModal
        :userRole="nonMemberToDelete"
        :showNonMemberModal="showNonMemberModal"
        :error="!!roleSubmitError"
        @remove-non-member-role="deleteRole(nonMemberToDelete, false)"
        @close-non-member-modal="closeNonMemberModal"
      />
      <AddNonMemberRoleModal
        :role="selectedFilter"
        :modelValue="showAddRoleModal"
        :existingMembers="teamMembers"
        :teamId="teamId"
        @close-add-non-member="toggleAddNonMemberRoleModal"
        @update:modelValue="toggleAddNonMemberRoleModal"
      />
      <EditMembersModal
        :showEditMembers="showEditMembersModal"
        :existingMembers="membersUserDetails"
        :teamId="teamId"
        :teamName="groupDetails?.name"
        @update:showEditMembers="toggleEditMembersModal"
      />
      <Modal
        v-if="inviteControlModel"
        v-model="showInviteControl"
        :title="`Invite people to join ${groupDetails?.name}`"
      >
        <InviteControl :model="inviteControlModel" />
      </Modal>
      <h1 data-test="gd-name">{{ groupDetails?.name }}</h1>
      <hr />
      <section>
        <!--        edit/delete-->
        <div class="margin-bottom flex">
          <a
            :href="`/admin/group/edit/${teamId}`"
            class="btn btn-default-edit"
            style="margin-right: 2rem"
            data-test="gd-edit-link"
            >Edit team</a
          >
          <a
            :href="`/admin/group/delete/${teamId}`"
            class="btn btn-default-close"
            data-test="gd-delete-link"
            >Delete team</a
          >
        </div>
        <hr />
        <div v-if="!!orgId" class="margin-bottom">
          <h4>Organisation</h4>
          <a
            :href="`/organisation/details/${orgId}`"
            class="btn btn-default"
            data-test="gd-org"
            role="link"
          >
            {{ groupDetails?.organisationName }}
          </a>
        </div>
        <!--        subscription info-->
        <div v-if="hasSubscription" class="margin-bottom">
          <h4>Subscription</h4>
          <p data-test="gd-subs">
            <span
              v-if="!groupDetails?.subscription.fixedDates?.startDate"
              data-test="gd-subs-no-fixed"
              >Subscriptions start when they Complete Solo</span
            >
            <span v-else data-test="gd-subs-fixed"
              >{{ groupDetails?.subscription.fixedDates.startDate }} to
              {{ groupDetails?.subscription.fixedDates.endDate }}</span
            >
            <br />
            <span data-test="gd-subs-duration"
              >Duration: {{ groupDetails?.subscription.durationInMonths }} months</span
            >
          </p>
        </div>
        <div v-if="!!groupDetails?.createdBy" class="margin-bottom" data-test="gd-created">
          <h4>Created by</h4>
          <a :href="`/admin/user/details/${groupDetails?.createdBy.id}`">{{
            groupDetails?.createdBy.name
          }}</a>
        </div>
        <!--        notes-->
        <div class="margin-bottom" data-test="gd-notes">
          <h4>Notes</h4>
          <p v-if="groupDetails?.notes">{{ groupDetails?.notes }}</p>
          <p v-else>No notes</p>
        </div>
        <!--        Scratchpad link-->
        <div class="margin-bottom">
          <a :href="scratchpadLink.href" data-test="gd-scratchpad-link">Go to scratchpad ✨</a>
        </div>
        <!--        Team summary (formally 'This group in Gyre People'-->
        <div class="margin-bottom" data-test="gd-summary-link">
          <a :href="`/team/watch/${teamId}`">Team Summary</a>
        </div>
        <!-- team links-->
        <div class="team-links margin-bottom">
          <a
            :href="groupDetails?.canViewSolo ? `/teamreport/view/${teamId}` : 'javascript:void(0)'"
            data-test="gd-team-solo-link"
            >Team Solo report</a
          >
          -
          <a :href="`/teamviewpointsreport/view/${teamId}`" data-test="gd-team-vp-link"
            >Team Viewpoints report</a
          >
          -
          <a :href="`/team/effectiveness/${teamId}`" data-test="gd-teq-link"
            >Team effectiveness reviews</a
          >
          -
          <a :href="`/admin/group/upload/${teamId}`" data-test="gd-upload-link"
            >Bulk upload users</a
          >
          -
          <a :href="`/admin/crmexport/group/${teamId}`" data-test="gd-crm-link">CRM Export</a>
          -
          <a :href="`/admin/crmexport/groupdata/${teamId}`" data-test="gd-data-export-link"
            >Data Export</a
          >
          -
          <a :href="`/admin/crmexport/groupterdata/${teamId}`" data-test="gd-ter-export-link"
            >TER Export</a
          >
          -
          <a :href="`/team/heatmap/${teamId}`" data-test="gd-heatmap-link">Heatmap</a>
          <div>
            -
            <a
              href="javascript:void(0)"
              @click="
                groupDetails?.hasManagedSubscription ? copySubInviteCode() : openInviteModal(false)
              "
              data-test="gd-invite-people-link"
              >Invite people to this group</a
            >
          </div>
          <div v-if="!groupDetails?.hasManagedSubscription">
            -
            <a
              href="javascript:void(0)"
              @click="openInviteModal(true)"
              data-test="gd-invite-person-link"
              >Invite one person to this group</a
            >
          </div>
        </div>
        <hr />
        <!--        role tabs-->
        <div class="row nav-row margin-bottom">
          <div class="col-sm-8">
            <ul class="nav nav-pills tab-list" role="tablist">
              <li
                role="presentation"
                :class="selectedFilter === GroupUserRoles.Member ? 'active' : ''"
              >
                <a
                  href="#members"
                  aria-controls="members"
                  role="tab"
                  data-toggle="tab"
                  data-test="gd-members-tab"
                  @click="applyFilter(GroupUserRoles.Member)"
                  >Members ({{ groupDetails?.groupMembers.length }})
                </a>
              </li>
              <li
                :class="selectedFilter === GroupUserRoles.Admin ? 'active' : ''"
                role="presentation"
              >
                <a
                  href="#teamleads"
                  aria-controls="team-leads"
                  role="tab"
                  data-toggle="tab"
                  data-test="gd-admins-tab"
                  @click="applyFilter(GroupUserRoles.Admin)"
                  >Team Leads ({{ teamAdmins.length }})</a
                >
              </li>
              <li
                role="presentation"
                :class="selectedFilter === GroupUserRoles.Watcher ? 'active' : ''"
              >
                <a
                  href="#watchers"
                  aria-controls="watchers"
                  role="tab"
                  data-toggle="tab"
                  data-test="gd-watchers-tab"
                  @click="applyFilter(GroupUserRoles.Watcher)"
                  >Watchers ({{ teamWatchers.length }})</a
                >
              </li>
            </ul>
          </div>
        </div>
        <div>
          <!--          Members tab-->
          <div
            v-if="selectedFilter === GroupUserRoles.Member"
            data-test="members-tab-info"
            id="members"
          >
            <h4>Members</h4>
            <p data-test="gd-members-count">{{ filteredMembers.length }} team members</p>
            <p data-test="gd-members-active-subs">
              {{ groupDetails?.activeSubscriptions }}
              {{ groupDetails?.activeSubscriptions === 1 ? "has" : "have" }} active subscriptions
            </p>
            <p data-test="gd-members-conflicting-subs">
              {{ groupDetails?.conflictingSubscriptions }}
              {{ groupDetails?.conflictingSubscriptions === 1 ? "has" : "have" }}
              conflicting subscriptions
            </p>
          </div>
          <!--Team Leads tab-->
          <div
            v-if="selectedFilter === GroupUserRoles.Admin"
            data-test="admins-tab-info"
            id="team-leads"
          >
            <h4>Team Leads</h4>
            <p data-test="gd-admins-count">
              {{ teamAdmins.length }} team lead{{ teamAdmins.length !== 1 ? "s" : "" }}
            </p>
            <div class="margin-bottom" v-if="nonMembersWithRoles.length">
              <p>Admins who aren't members of this team:</p>
              <ul class="non-member-role-list" data-test="non-member-admin-list">
                <li
                  v-for="nonMember in nonMembersWithRoles"
                  :key="nonMember.id"
                  class="non-member-list-item"
                  data-test="non-member-admin"
                >
                  <a :href="`/admin/user/details/${nonMember.id}`">{{ nonMember.name }}</a>
                  <button
                    class="btn btn-default"
                    @click="openNonMemberModal(nonMember)"
                    data-test="remove-non-member-admin"
                    style="max-width: 100px"
                    type="button"
                  >
                    Remove
                  </button>
                </li>
              </ul>
            </div>
            <button
              class="btn btn-default"
              @click="toggleAddNonMemberRoleModal(false)"
              data-test="add-non-member-admin"
              type="button"
            >
              Add team lead
            </button>
          </div>
          <!--          Watchers tab-->
          <div
            v-if="selectedFilter === GroupUserRoles.Watcher"
            data-test="watchers-tab-info"
            id="watchers"
          >
            <h4>Watchers</h4>
            <p data-test="gd-watchers-count">
              {{ teamWatchers.length }} watcher{{ teamWatchers.length !== 1 ? "s" : "" }}
            </p>
            <div v-if="nonMembersWithRoles.length" class="margin-bottom">
              <p>Watchers who aren't members of this team:</p>
              <ul class="non-member-role-list" data-test="non-member-watcher-list">
                <li
                  v-for="nonMember in nonMembersWithRoles"
                  :key="nonMember.id"
                  class="non-member-list-item"
                  data-test="non-member-watcher"
                >
                  <a :href="`/admin/user/details/${nonMember.id}`">{{ nonMember.name }}</a>
                  <button
                    class="btn btn-default"
                    @click="openNonMemberModal(nonMember)"
                    data-test="remove-non-member-watcher"
                    style="max-width: 100px"
                    type="button"
                  >
                    Remove
                  </button>
                </li>
              </ul>
            </div>
            <button
              class="btn btn-default"
              @click="toggleAddNonMemberRoleModal(false)"
              data-test="add-non-member-watcher"
              type="button"
            >
              Add watcher
            </button>
          </div>
          <br />
        </div>
        <div class="button-container">
          <button
            class="btn btn-primary"
            @click="toggleEditMembersModal(false)"
            data-test="gd-edit-members"
            type="button"
          >
            Add/Remove Members
          </button>
        </div>
        <br />
        <div class="table-container" data-test="gd-table-container">
          <GroupDetailsTable
            :key="selectedFilter"
            :teamMembers="filteredMembers"
            :teamId="teamId"
            @open-role-modal="openRoleModal"
            @toggle-licence-modal="toggleLicenceModal"
            @toggle-assign-licence-modal="toggleAssignLicenceModal"
          />
        </div>
        <br />
        <hr />
        <div>
          <div>
            <a :href="'/admin/group/list/teams'" data-test="gd-teams-link">Teams</a>
            |
            <a :href="'/admin/group/list/subgroups'" data-test="gd-subgroups-link"
              >Subscription Groups</a
            >
            |
            <a :href="'/admin/group/list/plaingroups'" data-test="gd-plain-link">Plain Groups</a>
            |
            <a :href="'/admin/group/list/allgroups'" data-test="gd-all-link">All</a>
          </div>
        </div>
      </section>
    </div>
    <LoadingPage v-else />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { Modal } from "uiv";
import { useRoute } from "vue-router";
import LoadingPage from "../../loading-page.vue";
import { GroupUserRoles } from "../../../components/admin/group/group-user-roles.enum";
import GroupDetailsTable from "../../../components/admin/group/group-details-table.vue";
import DeleteLicenceModal from "../../../components/admin/group/delete-licence-modal.vue";
import EditRoleModal from "../../../components/admin/group/edit-role-modal.vue";
import RemoveNonMemberRoleModal from "../../../components/admin/group/remove-non-member-role-modal.vue";
import AddNonMemberRoleModal from "../../../components/admin/group/add-non-member-role-modal.vue";
import EditMembersModal from "../../../components/admin/group/edit-group-members-modal.vue";
import AssignLicenceModal from "../../../components/admin/group/assign-licence-modal.vue";
import { HttpMethods, useAxios } from "../../../composables/api";
import {
  GroupDetailsApiModel,
  GroupUserRoleApiModel,
  InviteControlApiModel,
  LicenceApiModel,
  TeamCurrentMemberApiModel,
} from "../../../api-models";
import InviteControl from "../../../components/invite-control/invite-control.vue";

const route = useRoute();

const teamId: string = route.params.teamId as string;
const showLicenceModal = ref(false);
const showAssignLicenceModal = ref(false);
const memberWithLicence = ref<LicenceApiModel | null>(null);
const showRoleModal = ref(false);
const memberToEditRoleFor = ref<TeamCurrentMemberApiModel | null>(null);
const roleToEdit = ref<string>("");
let roleSubmitError = ref<number | null>(null);
const isAddingRole = ref<boolean | null>(null);
const nonMemberToDelete = ref<GroupUserRoleApiModel | null>(null);
const memberToAssignLicenceTo = ref<TeamCurrentMemberApiModel | null>(null);
const showNonMemberModal = ref(false);
const showAddRoleModal = ref(false);
const showEditMembersModal = ref(false);
const selectedFilter = ref<GroupUserRoles>(GroupUserRoles.Member);
const showInviteControl = ref(false);
const inviteModel = ref<InviteControlApiModel | null>(null);
const isWaitingForData = ref(false);

const { data: groupDetails, refreshData } = useAxios<GroupDetailsApiModel>(
  `/api/admin/groupdetails/${teamId}`,
);

const refreshGroupDetails = async () => {
  isWaitingForData.value = true;
  await refreshData();
  isWaitingForData.value = false;
};

const { data: inviteControlMultipleUseModel } = useAxios<InviteControlApiModel>(
  `/api/admin/groupinvite/${teamId}`,
);

const { data: inviteControlSingleUseModel } = useAxios<InviteControlApiModel>(
  `/api/admin/groupinvite/${teamId}?isSingleUse=true&allowServerSend=false`,
);

const isGroupDetailsLoaded = computed(() => !!groupDetails.value);

const scratchpadLink = computed(() => {
  const url = new URL("/scratchpad", window.location.origin);
  url.searchParams.set("subject", "Team");
  url.searchParams.set("id", teamId);
  return url;
});

const teamMembers = computed(() => {
  return groupDetails.value?.groupMembers ?? [];
});

const membersUserDetails = computed({
  get: () => {
    return (
      groupDetails.value?.groupMembers.map((m) => {
        return m.userDetails;
      }) ?? []
    );
  },
  set: (newValue) => {
    return newValue;
  },
});

const hasSubscription = computed(() => {
  return !!groupDetails.value?.subscription?.durationInMonths;
});

const teamAdmins = computed(() => {
  const roles = groupDetails.value?.roles ?? [];

  if (!roles.length) {
    return [];
  }

  return roles.filter((role) => {
    return role.role.toLowerCase() === GroupUserRoles.Admin;
  });
});

const teamWatchers = computed(() => {
  const roles = groupDetails.value?.roles ?? [];

  if (!roles.length) {
    return [];
  }

  return roles.filter((role) => {
    return role.role.toLowerCase() === GroupUserRoles.Watcher;
  });
});

const nonMemberRoles = (
  roles: GroupUserRoleApiModel[],
  role: GroupUserRoles,
): GroupUserRoleApiModel[] => {
  const filterByRole = roles.filter((r) => {
    return r.role === role;
  });
  const memberIds: string[] = [];
  teamMembers?.value?.forEach((m) => {
    memberIds.push(m.userDetails.id);
  });
  return filterByRole.filter((r) => {
    return !memberIds.includes(r.id);
  });
};

const nonMembersWithRoles = computed(() => {
  if (groupDetails.value) {
    if (selectedFilter.value === GroupUserRoles.Admin) {
      return "roles" in groupDetails.value
        ? nonMemberRoles(groupDetails.value.roles, GroupUserRoles.Admin)
        : [];
    }

    if (selectedFilter.value === GroupUserRoles.Watcher) {
      return "roles" in groupDetails.value
        ? nonMemberRoles(groupDetails.value.roles, GroupUserRoles.Watcher)
        : [];
    }
  }
  return [];
});

const filteredMembers = computed(() => {
  const groupMembers = groupDetails.value?.groupMembers || [];

  if (!groupMembers.length) {
    return [];
  }

  if (selectedFilter.value === GroupUserRoles.Admin) {
    return groupMembers.filter((gm) => {
      return gm.roles.some((role) => role.role.toLowerCase() === "admin");
    });
  }

  if (selectedFilter.value === GroupUserRoles.Watcher) {
    return groupMembers.filter((gm) => {
      return gm.roles.some((role) => role.role.toLowerCase() === "watcher");
    });
  }

  return groupMembers;
});

const orgId = computed(() => {
  return groupDetails.value?.organisationId;
});

const inviteControlModel = computed({
  get: () => {
    return inviteModel.value;
  },
  set: (newValue) => {
    inviteModel.value = newValue as InviteControlApiModel;
  },
});

const applyFilter = (filter: GroupUserRoles): void => {
  selectedFilter.value = filter;
};

const addingOrRemovingRole = (member: TeamCurrentMemberApiModel, role: string): void => {
  isAddingRole.value =
    member.roles.filter((r) => {
      return (
        r.role === role.toLowerCase() && r.groupId === teamId && r.id === member.userDetails.id
      );
    }).length === 0;
};

const openRoleModal = (member: TeamCurrentMemberApiModel, role: string): void => {
  addingOrRemovingRole(member, role);
  memberToEditRoleFor.value = member;
  showRoleModal.value = true;
  roleToEdit.value = role.toLowerCase();
};

const closeRoleModal = (): void => {
  showRoleModal.value = false;
  roleSubmitError = ref<number | null>(null);
  isAddingRole.value = null;
};

const toggleLicenceModal = (member?: LicenceApiModel): void => {
  if (member) {
    memberWithLicence.value = member;
  }
  showLicenceModal.value = !showLicenceModal.value;
};

const toggleAssignLicenceModal = (member?: TeamCurrentMemberApiModel): void => {
  showAssignLicenceModal.value = !showAssignLicenceModal.value;
  if (!member) {
    memberToAssignLicenceTo.value = null;
  } else {
    memberToAssignLicenceTo.value = member;
  }
};

const openNonMemberModal = (userRole: GroupUserRoleApiModel): void => {
  nonMemberToDelete.value = userRole;
  showNonMemberModal.value = true;
};

const closeNonMemberModal = (): void => {
  showNonMemberModal.value = false;
};

const toggleAddNonMemberRoleModal = (isRefreshNeeded?: boolean): void => {
  showAddRoleModal.value = !showAddRoleModal.value;
  if (isRefreshNeeded) {
    refreshGroupDetails();
  }
};

const toggleEditMembersModal = (isRefreshNeeded?: boolean): void => {
  showEditMembersModal.value = !showEditMembersModal.value;
  if (isRefreshNeeded) {
    refreshGroupDetails();
  }
};

const openInviteModal = (isForOnePerson: boolean): void => {
  if (isForOnePerson) {
    inviteControlModel.value = inviteControlSingleUseModel.value;
  } else {
    inviteControlModel.value = inviteControlMultipleUseModel.value;
  }
  showInviteControl.value = true;
};

const removeLicenceForMember = (id: string): void => {
  const { promise } = useAxios(`/api/admin/removelicence/${id}`, { httpMethod: "DELETE" });
  promise.then(() => {
    toggleLicenceModal();
    refreshGroupDetails();
  });
};

const assignLicenceToMember = (id: string): void => {
  const { promise } = useAxios(`/api/admin/assignlicence/${id}`, { httpMethod: "POST" });
  promise.then(() => {
    toggleAssignLicenceModal();
    refreshGroupDetails();
  });
};

const deleteRole = async (userRole: GroupUserRoleApiModel, isMember: boolean): Promise<void> => {
  const { promise, errorStatus } = useAxios("/api/admin/teamrole", {
    data: userRole,
    isFatal: false,
    httpMethod: "DELETE" as HttpMethods,
  });
  roleSubmitError = errorStatus;
  promise.then(() => {
    if (isMember) {
      closeRoleModal();
    } else {
      closeNonMemberModal();
    }

    refreshGroupDetails();
  });
};

const submitEditRole = async (member: TeamCurrentMemberApiModel, role: string): Promise<void> => {
  const memberFullName = `${member.userDetails.firstName} ${member.userDetails.lastName}`;
  const editUserRole: GroupUserRoleApiModel = {
    id: member.userDetails.id,
    groupId: teamId,
    name: memberFullName,
    role: role.toLowerCase(),
  };

  if (isAddingRole.value) {
    const { promise, errorStatus } = useAxios<GroupUserRoleApiModel>("/api/admin/teamrole", {
      isFatal: false,
      data: editUserRole,
      httpMethod: "POST" as HttpMethods,
    });
    roleSubmitError = errorStatus;
    promise.then(() => {
      closeRoleModal();
      refreshGroupDetails();
    });
  } else {
    await deleteRole(editUserRole, true);
  }
};

const copySubInviteCode = async () => {
  try {
    if (inviteControlMultipleUseModel.value) {
      await navigator.clipboard.writeText(inviteControlMultipleUseModel.value.inviteUrl);
      // eslint-disable-next-line no-alert
      alert("Invite code copied to clipboard!");
    }
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert("Cannot copy");
  }
};

watchEffect(() => {
  if (!showInviteControl.value) {
    setTimeout(() => {
      inviteControlModel.value = null;
    }, 1000);
  }
});
</script>

<style scoped lang="less">
@import "../../../styles/bootstrap/variables.less";

.group-details-view {
  width: 100%;

  @media (min-width: 1375px) {
    display: flex;
    justify-content: center;
  }
}

.page-container {
  margin: 0 5rem;
  max-width: 1800px;

  @media (max-width: @screen-sm-max) {
    margin: 0 1rem;
  }
}

.flex {
  display: flex;
}

.button-container {
  display: flex;
  gap: 2rem;
}

.non-member-role-list {
  width: clamp(250px, 40%, 500px);
  border-radius: 15px;
  box-shadow: 1px 1px 15px 5px rgba(238, 238, 238, 0.51);
}

.non-member-list-item {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  align-items: center;
  padding: 1rem;

  p {
    margin-bottom: 0;
  }
}

.team-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.margin-bottom {
  margin-bottom: 2rem;
}

.table-container {
  overflow-x: scroll;
}
</style>
